import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useTenantPath } from "@/lib/tenantPath";
import PhotoEditorModal from "@/components/PhotoEditorModal";
import { ArrowLeft, Loader2, Camera } from "lucide-react";

const ALL_TAGS = ["hostess", "promotor", "coordenador", "bilingue", "premium", "modelo", "recepção", "experiente"];

export default function PromoterForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const tPath = useTenantPath();
  const isEdit = Boolean(id);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", nif: "", photo_url: "",
    location: "", tags: [], active: true, notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState("");
  const [photoModal, setPhotoModal] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/promoters/${id}`).then((r) => {
      const { name, email, phone, nif, photo_url, location, tags, active, notes } = r.data;
      setForm({ name: name || "", email: email || "", phone: phone || "", nif: nif || "", photo_url: photo_url || "", location: location || "", tags: tags || [], active: active ?? true, notes: notes || "" });
    }).finally(() => setLoading(false));
  }, [id, isEdit]);

  const toggleTag = (t) => {
    setForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      if (isEdit) await api.put(`/promoters/${id}`, form);
      else await api.post("/promoters", form);
      navigate(tPath("/promotores"));
    } catch (err) {
      setError(err.response?.data?.detail || "Erro a guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-zinc-500">A carregar…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="promoter-form-page">
      <Link to={tPath("/promotores")} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>
      <h1 className="text-3xl font-display font-bold tracking-tight text-zinc-950">{isEdit ? "Editar promotor" : "Novo promotor"}</h1>

      <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5">
        <Field label="Nome" required>
          <input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Email"><input data-testid="form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="form-input" /></Field>
          <Field label="Telefone"><input data-testid="form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="form-input" placeholder="+351 912 345 678" /></Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Localidade"><input data-testid="form-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="form-input" placeholder="Lisboa, Porto…" /></Field>
          <Field label="NIF"><input data-testid="form-nif" value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} className="form-input" placeholder="999 999 999" /></Field>
        </div>

        <Field label="Fotografia">
          <div className="flex items-center gap-4">
            {form.photo_url ? (
              <img src={form.photo_url} alt="" data-testid="promoter-avatar-preview" className="w-16 h-16 rounded-full object-cover bg-zinc-100 border border-zinc-200" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 text-lg font-semibold">
                {(form.name || "").trim().split(" ").filter(Boolean).map((n) => n[0]).slice(0, 2).join("").toUpperCase() || "?"}
              </div>
            )}
            <button
              type="button"
              onClick={() => setPhotoModal(true)}
              data-testid="open-photo-editor"
              className="inline-flex items-center gap-2 border border-zinc-200 hover:bg-zinc-50 px-4 py-2 rounded-lg text-sm font-semibold text-zinc-800"
            >
              <Camera className="w-4 h-4" /> {form.photo_url ? "Editar fotografia" : "Adicionar fotografia"}
            </button>
          </div>
        </Field>

        <PhotoEditorModal
          open={photoModal}
          initialPhoto={form.photo_url}
          onClose={() => setPhotoModal(false)}
          onSave={(dataUrl) => setForm((f) => ({ ...f, photo_url: dataUrl }))}
          onRemove={() => setForm((f) => ({ ...f, photo_url: "" }))}
        />

        <Field label="Tags">
          <div className="flex flex-wrap gap-2">
            {ALL_TAGS.map((t) => (
              <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`tag-${t}`} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${form.tags.includes(t) ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}>
                {t}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Notas"><textarea data-testid="form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="form-input" /></Field>

        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="form-active" />
          Promotor ativo
        </label>

        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{String(error)}</div>}

        <div className="flex gap-3 pt-2 border-t border-zinc-100">
          <button type="submit" disabled={saving} data-testid="form-submit" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Guardar alterações" : "Criar promotor"}
          </button>
          <Link to={tPath("/promotores")} className="px-5 py-2.5 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">Cancelar</Link>
        </div>
      </form>

      <style>{`.form-input{width:100%;padding:0.625rem 0.75rem;border-radius:0.5rem;border:1px solid #e4e4e7;font-size:0.875rem;background:#fff} .form-input:focus{outline:none;border-color:#09090b;box-shadow:0 0 0 2px #09090b}`}</style>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">{label}{required && <span className="text-red-600"> *</span>}</label>
      {children}
    </div>
  );
}
